import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useArtifacts, useCreateArtifact } from "@/api/queries";
import type { Artifact, Json } from "@/api/types";
import { ArtifactDetail } from "@/components/ArtifactDetail";
import { Button } from "@/components/Button";
import { ChoiceItem } from "@/components/ChoiceItem";
import { DefinitionList } from "@/components/DefinitionList";
import { EmptyState } from "@/components/EmptyState";
import { Field, TextArea, TextInput } from "@/components/Field";
import { Modal } from "@/components/Modal";
import { RemoteState } from "@/components/RemoteState";
import { SegmentControl } from "@/components/SegmentControl";
import { Tag } from "@/components/Tag";
import { cn } from "@/lib/cn";
import { useUiStore, type GraphMode } from "@/store/ui";

/** @dataSource remote — 故事图完全由项目内 `storylet` Artifact 构建 */

interface StoryChoice {
  label: string;
  effect: string;
  affects: string[];
}

interface StoryNode {
  id: string;
  code: string;
  title: string;
  x: number;
  y: number;
  kind: string;
  summary: string;
  goal: string;
  entry: string;
  updates: string[];
  assets: string;
  choices: StoryChoice[];
}

const GRAPH_MODES: { value: GraphMode; label: string }[] = [
  { value: "structure", label: "结构" },
  { value: "state", label: "状态" },
  { value: "assets", label: "素材" },
];

/** 节点几何常量，沿用原型比例（NODE_W=176，垂直锚点在 y+44）。 */
const NODE_WIDTH = 176;
const NODE_ANCHOR_Y = 44;
const COL_STEP = 262;
const ROW_STEP = 148;
const ROW_LIMIT = 4;

function asString(value: Json | undefined, fallback = ""): string {
  return typeof value === "string" ? value : fallback;
}

function asStringList(value: Json | undefined): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string")
    : [];
}

/** 取某逻辑键下版本号最高的 Artifact（后端按逻辑键维护版本链）。 */
function latestOfType(items: Artifact[], type: string): Artifact | undefined {
  return items.filter((item) => item.type === type).sort((a, b) => b.version - a.version)[0];
}

interface RawStorylet {
  id: string;
  title: string;
  next: string[];
  types: string[];
  reads: string[];
  readText: string;
  sceneTask: string;
  entry: string;
  assets: string;
  choiceId: string;
}

function readStorylets(artifact: Artifact | undefined): { start: string; items: RawStorylet[] } {
  const content = artifact?.content ?? {};
  const raw = Array.isArray(content.storylets) ? content.storylets : [];
  const items = raw
    .filter((item): item is Record<string, Json> => typeof item === "object" && item !== null)
    .map((item) => {
      const cast = asStringList(item.cast);
      const reads = asStringList(item.reads);
      return {
        id: asString(item.id),
        title: asString(item.title, asString(item.id)),
        next: asStringList(item.next),
        types: asStringList(item.type),
        reads,
        readText: reads.length ? reads.join(" · ") : "无状态读取",
        sceneTask: asString(item.scene_task, "未填写戏剧任务"),
        entry: asString(item.location, "未绑定地点"),
        assets: cast.length
          ? `${cast.length} 名出场角色 · ${asString(item.location, "未绑定地点")}`
          : "未估算",
        choiceId: asString(item.choice_id),
      };
    })
    .filter((item) => item.id);

  return { start: asString(content.start, items[0]?.id ?? ""), items };
}

/** 分层布局：按 `next` 图从起点做最长路径分层，同一层按出现顺序排列。 */
function layout(items: RawStorylet[], start: string): Map<string, { x: number; y: number }> {
  const depth = new Map<string, number>();
  const byId = new Map(items.map((item) => [item.id, item]));
  const queue: string[] = [];

  if (start && byId.has(start)) {
    depth.set(start, 0);
    queue.push(start);
  }
  for (const item of items) {
    if (!depth.has(item.id)) {
      depth.set(item.id, 0);
      queue.push(item.id);
    }
  }

  while (queue.length) {
    const current = queue.shift()!;
    const currentDepth = depth.get(current) ?? 0;
    for (const next of byId.get(current)?.next ?? []) {
      if (!byId.has(next)) continue;
      const candidate = currentDepth + 1;
      if (candidate > (depth.get(next) ?? -1)) {
        depth.set(next, candidate);
        queue.push(next);
      }
    }
  }

  const perColumn = new Map<number, number>();
  const positions = new Map<string, { x: number; y: number }>();
  for (const item of items) {
    const column = depth.get(item.id) ?? 0;
    const row = perColumn.get(column) ?? 0;
    perColumn.set(column, row + 1);
    positions.set(item.id, {
      x: 76 + column * COL_STEP,
      y: 60 + (row % ROW_LIMIT) * ROW_STEP + Math.floor(row / ROW_LIMIT) * 72,
    });
  }
  return positions;
}

function kindOf(item: RawStorylet): string {
  const types = item.types.map((value) => value.toUpperCase());
  if (item.id.startsWith("END") || types.includes("ENDING")) return "ending";
  if (types.includes("ANCHOR")) return "locked";
  if (types.includes("RISK")) return "risk";
  return "storylet";
}

function choicesOf(choiceArtifact: Artifact | undefined, choiceId: string): StoryChoice[] {
  if (!choiceId) return [];
  const pool = Array.isArray(choiceArtifact?.content?.choices)
    ? choiceArtifact!.content.choices
    : [];
  const node = pool.find(
    (item): item is Record<string, Json> =>
      typeof item === "object" &&
      item !== null &&
      !Array.isArray(item) &&
      asString((item as Record<string, Json>).id) === choiceId,
  );
  const options = Array.isArray(node?.options) ? node!.options : [];
  return options
    .filter(
      (item): item is Record<string, Json> =>
        typeof item === "object" && item !== null && !Array.isArray(item),
    )
    .map((option) => {
      const delta = option.state_delta;
      const affects =
        typeof delta === "object" && delta !== null
          ? Object.keys(delta as Record<string, Json>)
          : [];
      return {
        label: asString(option.action, asString(option.id)),
        effect: asString(option.motivation, "未填写动机"),
        affects,
      };
    });
}

/**
 * 故事结构工作台。
 *
 * 数据来自项目内 `storylet` Artifact：节点=Storylet，边=`next`，坐标由分层布局算出。
 * 不存在静态兜底图——项目没有 storylet 时呈现空态。
 */
export function StoryView() {
  const navigate = useNavigate();
  const projectId = useUiStore((state) => state.projectId);
  const graphMode = useUiStore((state) => state.graphMode);
  const setGraphMode = useUiStore((state) => state.setGraphMode);
  const graphZoom = useUiStore((state) => state.graphZoom);
  const zoomIn = useUiStore((state) => state.zoomIn);
  const zoomOut = useUiStore((state) => state.zoomOut);
  const selectedNodeId = useUiStore((state) => state.selectedNodeId);
  const affectedNodes = useUiStore((state) => state.affectedNodes);
  const selectNode = useUiStore((state) => state.selectNode);
  const pushToast = useUiStore((state) => state.pushToast);

  const artifacts = useArtifacts(projectId);
  const createArtifact = useCreateArtifact(projectId);

  const [modal, setModal] = useState<"branch" | "edit" | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [branchTitle, setBranchTitle] = useState("");
  const [branchGoal, setBranchGoal] = useState("");
  const [branchParent, setBranchParent] = useState("");

  const storyletArtifact = useMemo(
    () => latestOfType(artifacts.data ?? [], "storylet"),
    [artifacts.data],
  );
  const choiceArtifact = useMemo(
    () => latestOfType(artifacts.data ?? [], "choice"),
    [artifacts.data],
  );

  const { start, items } = useMemo(() => readStorylets(storyletArtifact), [storyletArtifact]);

  const nodes = useMemo<StoryNode[]>(() => {
    const positions = layout(items, start);
    return items.map((item) => {
      const pos = positions.get(item.id) ?? { x: 76, y: 60 };
      return {
        id: item.id,
        code: item.id,
        title: item.title,
        x: pos.x,
        y: pos.y,
        kind: kindOf(item),
        summary: item.sceneTask,
        goal: item.sceneTask,
        entry: item.entry,
        updates: item.reads,
        assets: item.assets,
        choices: choicesOf(choiceArtifact, item.choiceId),
      };
    });
  }, [items, start, choiceArtifact]);

  const node = nodes.find((item) => item.id === selectedNodeId) ?? nodes[0];

  const edges = useMemo<[string, string][]>(
    () => items.flatMap((item) => item.next.map((next) => [item.id, next] as [string, string])),
    [items],
  );

  const canvasWidth = nodes.length
    ? Math.max(1200, Math.max(...nodes.map((item) => item.x)) + NODE_WIDTH + 120)
    : 1200;

  return (
    <RemoteState queries={[artifacts]} title="故事结构加载失败">
      <div className="story-workbench">
        <section className="story-main">
          <div className="view-toolbar">
            <div className="toolbar-left">
              <SegmentControl<GraphMode>
                label="故事图视图"
                value={graphMode}
                onChange={setGraphMode}
                options={GRAPH_MODES}
              />
              <span className="tag">
                {nodes.length} 节点 · {edges.length} 条转移
              </span>
            </div>
            <div className="toolbar-right">
              <Button
                size="small"
                variant="quiet"
                id="storylet-detail"
                disabled={!storyletArtifact}
                onClick={() => setDetailId(storyletArtifact?.id ?? null)}
              >
                结构产物详情
              </Button>
              <Button
                size="small"
                variant="quiet"
                id="add-branch"
                disabled={!storyletArtifact}
                onClick={() => {
                  setBranchParent(node?.id ?? start);
                  setModal("branch");
                }}
              >
                <span aria-hidden="true">＋</span>新建分支
              </Button>
              <div className="zoom-control" aria-label="缩放故事图">
                <button type="button" title="缩小" onClick={zoomOut}>
                  −
                </button>
                <span>{Math.round(graphZoom * 100)}%</span>
                <button type="button" title="放大" onClick={zoomIn}>
                  ＋
                </button>
              </div>
            </div>
          </div>

          <div className="graph-viewport" id="graph-viewport">
            {nodes.length === 0 ? (
              <EmptyState
                title="还没有故事结构"
                description="结构由 storylet Artifact 定义。请先在生成中心用「结构工程师」产出 Storylet 池，或手工新建一个。"
                action={
                  <Button variant="primary" onClick={() => navigate("/studio/generate")}>
                    去生成中心
                  </Button>
                }
              />
            ) : (
              <div className="graph-canvas" style={{ width: `${canvasWidth}px`, zoom: graphZoom }}>
                <svg
                  className="graph-lines"
                  viewBox={`0 0 ${canvasWidth} 700`}
                  preserveAspectRatio="none"
                  aria-hidden="true"
                >
                  {edges.map(([fromId, toId]) => {
                    const from = nodes.find((item) => item.id === fromId);
                    const to = nodes.find((item) => item.id === toId);
                    if (!from || !to) return null;
                    const sx = from.x + NODE_WIDTH;
                    const sy = from.y + NODE_ANCHOR_Y;
                    const tx = to.x;
                    const ty = to.y + NODE_ANCHOR_Y;
                    const bend = Math.max(58, (tx - sx) * 0.42);
                    const highlighted =
                      affectedNodes.includes(toId) ||
                      selectedNodeId === fromId ||
                      selectedNodeId === toId;
                    return (
                      <path
                        key={`${fromId}-${toId}`}
                        className={highlighted ? "is-highlighted" : undefined}
                        d={`M ${sx} ${sy} C ${sx + bend} ${sy}, ${tx - bend} ${ty}, ${tx} ${ty}`}
                      />
                    );
                  })}
                </svg>

                {nodes.map((item) => {
                  const kindClass =
                    item.kind === "ending"
                      ? "is-ending"
                      : item.kind === "locked"
                        ? "is-locked"
                        : item.kind === "risk"
                          ? "is-risk"
                          : "";
                  const meta =
                    graphMode === "assets"
                      ? item.assets
                      : graphMode === "state"
                        ? item.updates.slice(0, 2).join(" · ")
                        : item.summary;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      className={cn(
                        "story-node",
                        kindClass,
                        item.id === selectedNodeId && "is-selected",
                        affectedNodes.includes(item.id) && "is-affected",
                      )}
                      style={{ left: item.x, top: item.y }}
                      aria-label={`${item.code} ${item.title}`}
                      aria-pressed={item.id === selectedNodeId}
                      onClick={() => selectNode(item.id, [])}
                    >
                      <span className="node-port in" aria-hidden="true"></span>
                      <span className="node-port out" aria-hidden="true"></span>
                      <small>{item.code}</small>
                      <strong>{item.title}</strong>
                      <span>{meta.length > 52 ? `${meta.slice(0, 52)}…` : meta}</span>
                    </button>
                  );
                })}
              </div>
            )}

            <div className="graph-legend" aria-label="节点图例">
              <span className="legend-item">
                <span className="legend-dot"></span>固定锚点
              </span>
              <span className="legend-item">
                <span className="legend-dot locked"></span>不可逆选择
              </span>
              <span className="legend-item">
                <span className="legend-dot risk"></span>风险节点
              </span>
              <span className="legend-item">
                <span className="legend-dot ending"></span>配置结局
              </span>
            </div>
          </div>
        </section>

        {node ? (
          <Inspector
            node={node}
            onChoice={(choice) => selectNode(node.id, choice.affects)}
            onEdit={() => setModal("edit")}
            onOpenScript={() => navigate("/studio/script")}
          />
        ) : (
          <aside className="inspector" aria-label="节点详情">
            <EmptyState title="未选中节点" />
          </aside>
        )}

        <Modal
          open={modal === "branch"}
          onClose={() => setModal(null)}
          eyebrow="故事结构"
          title="新建分支"
          footer={
            <>
              <Button variant="quiet" onClick={() => setModal(null)}>
                取消
              </Button>
              <Button
                variant="primary"
                disabled={!branchTitle.trim() || createArtifact.isPending}
                onClick={() => {
                  const parent = nodes.find((item) => item.id === branchParent) ?? nodes[0];
                  createArtifact.mutate(
                    {
                      type: "storylet",
                      logical_key: `storylet:${branchTitle.trim()}`,
                      title: branchTitle.trim(),
                      parents: storyletArtifact ? [storyletArtifact.id] : [],
                      content: {
                        id: branchTitle.trim(),
                        title: branchTitle.trim(),
                        next: [],
                        type: ["STORYLET"],
                        reads: [],
                        scene_task: branchGoal.trim(),
                        location: parent?.entry ?? "",
                      },
                    },
                    {
                      onSuccess: () => {
                        setBranchTitle("");
                        setBranchGoal("");
                        setModal(null);
                        pushToast({
                          title: "分支已创建",
                          detail: "已生成 draft 版本，需通过结构检查与 G4 互动锁定后才能标记正式",
                          mark: "✓",
                        });
                      },
                    },
                  );
                }}
              >
                {createArtifact.isPending ? "创建中…" : "创建分支"}
              </Button>
            </>
          }
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <Field id="branch-title" label="分支标题" required>
              <TextInput
                id="branch-title"
                value={branchTitle}
                placeholder="例如：迎儿的证词"
                onChange={(event) => setBranchTitle(event.target.value)}
              />
            </Field>
            <Field
              id="branch-goal"
              label="戏剧任务"
              hint="这个分支要测试玩家什么？不允许只改变语气。"
            >
              <TextArea
                id="branch-goal"
                rows={3}
                value={branchGoal}
                placeholder="例如：测试玩家是否愿意为一个弱者的证词承担暴露风险"
                onChange={(event) => setBranchGoal(event.target.value)}
              />
            </Field>
            <Field id="branch-parent" label="挂靠节点">
              <div className="select-wrap">
                <select
                  id="branch-parent"
                  value={branchParent}
                  onChange={(event) => setBranchParent(event.target.value)}
                >
                  {nodes.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.code} · {item.title}
                    </option>
                  ))}
                </select>
                <span aria-hidden="true">⌄</span>
              </div>
            </Field>
            {createArtifact.isError ? (
              <p className="inline-note">
                {(createArtifact.error as Error)?.message ?? "创建失败"}
              </p>
            ) : null}
          </div>
        </Modal>

        {node ? (
          <Modal
            open={modal === "edit"}
            onClose={() => setModal(null)}
            eyebrow={node.code}
            title={`节点 · ${node.title}`}
            footer={
              <Button variant="quiet" onClick={() => setModal(null)}>
                关闭
              </Button>
            }
          >
            <DefinitionList
              entries={[
                { term: "节点 ID", value: node.id },
                { term: "类型", value: node.kind },
                { term: "戏剧任务", value: node.goal },
                { term: "进入条件", value: node.entry },
                { term: "状态读取", value: node.updates },
              ]}
            />
          </Modal>
        ) : null}
      </div>

      <ArtifactDetail artifactId={detailId} onClose={() => setDetailId(null)} />
    </RemoteState>
  );
}

function Inspector({
  node,
  onChoice,
  onEdit,
  onOpenScript,
}: {
  node: StoryNode;
  onChoice: (choice: StoryChoice) => void;
  onEdit: () => void;
  onOpenScript: () => void;
}) {
  return (
    <aside className="inspector" aria-label="节点详情">
      <header className="inspector-header">
        <div className="meta-row">
          <Tag tone={node.kind === "risk" ? "red" : node.kind === "locked" ? "gold" : "green"}>
            {node.code}
          </Tag>
        </div>
        <h2>{node.title}</h2>
        <p>{node.summary}</p>
      </header>

      <section className="inspector-section">
        <h3>本场戏剧任务</h3>
        <p>{node.goal}</p>
      </section>

      <section className="inspector-section">
        <h3>进入与退出</h3>
        <DefinitionList
          entries={[
            { term: "进入条件", value: node.entry },
            { term: "状态更新", value: node.updates },
            { term: "制作影响", value: node.assets },
          ]}
        />
      </section>

      {node.choices.length ? (
        <section className="inspector-section">
          <h3>玩家行动 · 选择一个查看影响</h3>
          <div className="choice-list">
            {node.choices.map((choice, index) => (
              <ChoiceItem
                key={choice.label}
                index={index}
                label={choice.label}
                effect={choice.effect}
                onClick={() => onChoice(choice)}
              />
            ))}
          </div>
        </section>
      ) : null}

      <div className="inspector-actions">
        <Button variant="quiet" id="edit-node" onClick={onEdit}>
          查看节点
        </Button>
        <Button variant="primary" id="open-script" onClick={onOpenScript}>
          打开脚本
        </Button>
      </div>
    </aside>
  );
}
